import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { tariffColumns } from '../internals/data/gridData';
import LoadingData from './LoadingData';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid2 from '@mui/material/Grid2';
import HorizontalScrollIndicator from './HorizontalScrollIndicator';
import { useTheme } from '@mui/material/styles';

interface TariffDataItem {
  full_name: string;
  start_date: string;
  end_date: string;
  value_exc_vat: number;
  value_inc_vat: number;
  cost_exc_vat: number;
  cost_inc_vat: number;
  payment_method_id: number;
}

interface DataGridProps {
  data: TariffDataItem[];
  loading: boolean;
}

const getRowId = (row: any) => `${row.full_name}_${row.start_date}_${row.end_date}`;

const TariffDataGrid: React.FC<DataGridProps> = ({ data, loading }) => {  
  const theme = useTheme();
  let columns = tariffColumns;

  // If there's exactly one tariff, display it as a Card
  if (data.length === 1) {
    const tariff = data[0];
    
    return (
      <Box sx={{ width: '100%', position: 'relative' }}>
        <LoadingData open={loading} />
        <Card sx={{ 
          backgroundColor: theme.palette.background.paper
        }}>
          <CardContent>
            {/* Mobile Layout: Stacked */}
            <Box sx={{ 
              display: { xs: 'block', md: 'none' }
            }}>
              {/* Tariff Name - Full Width */}
              <Box sx={{ mb: 2, px: 1 }}>
                <Typography variant="h4">
                  {tariff.full_name}
                </Typography>
              </Box>
              
              {/* Tariff Details - Row Below */}
              <Grid2 container spacing={2}>
                <Grid2 size={3}>
                  <Typography variant="h6" sx={{ pl: 1 }}>
                    Start Date
                  </Typography>
                  <Typography variant="subtitle1" sx={{ pl: 1 }}>
                    {new Date(tariff.start_date).toLocaleDateString('en-GB')}
                  </Typography>
                </Grid2>
                <Grid2 size={3}>
                  <Typography variant="h6" sx={{ pl: 1 }}>
                    End Date
                  </Typography>
                  <Typography variant="subtitle1" sx={{ pl: 1 }}>
                    {new Date(tariff.end_date).toLocaleDateString('en-GB')}
                  </Typography>
                </Grid2>
                <Grid2 size={6}>
                  <Typography variant="h6" sx={{ pl: 1 }}>
                    Standing Fee
                  </Typography>
                  <Typography variant="subtitle1" sx={{ pl: 1 }}>
                  £{tariff.cost_inc_vat.toFixed(2)} ({tariff.value_inc_vat.toFixed(2)}p/day)
                  </Typography>
                </Grid2>                
              </Grid2>
            </Box>

            {/* Desktop Layout: Side by Side */}
            <Box sx={{ 
              display: { xs: 'none', sm: 'none', md: 'flex' }, 
              alignItems: 'center', 
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h3">
                  {tariff.full_name}
                </Typography>
              </Box>
              
              <Grid2 container spacing={2} sx={{ minWidth: 600 }}>
                <Grid2 size={3}>
                  <Typography variant="h5">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(tariff.start_date).toLocaleDateString('en-GB')}
                  </Typography>
                </Grid2>
                <Grid2 size={3}>
                  <Typography variant="h5">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(tariff.end_date).toLocaleDateString('en-GB')}
                  </Typography>
                </Grid2>
                <Grid2 size={6}>
                  <Typography variant="h5">
                    Standing Fee
                  </Typography>
                  <Typography variant="body1">
                  £{tariff.cost_inc_vat.toFixed(2)}  ({tariff.value_inc_vat.toFixed(2)}p/day)
                  </Typography>
                </Grid2>
              </Grid2>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // If there are multiple tariffs, display as DataGrid
  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <LoadingData open={loading} />
      <DataGrid
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: "normal"
          },
          "& .MuiDataGrid-columnHeader": {
            // Forced to use important since overriding inline styles
            height: "unset !important"
          },
          position: 'relative'
        }}
        autoHeight
        rows={data}
        columns={columns}
        rowHeight={40}
        getRowId={getRowId}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        hideFooter
        disableColumnResize
        density="compact"
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
      />
      <HorizontalScrollIndicator />
    </Box>
  );
}


export default TariffDataGrid